import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ExternalLink,
  Filter,
  Inbox,
  Info,
  Loader2,
  RefreshCw,
} from "lucide-react";
import Button from "./ui/Button";
import { Sheet, SheetContent } from "./ui";
import { cn } from "../lib/utils";

const SEVERITY_STYLES = {
  INFO: {
    Icon: Info,
    icon: "text-info",
    label: "Informação",
  },
  WARNING: {
    Icon: AlertTriangle,
    icon: "text-warning",
    label: "Atenção",
  },
  SUCCESS: {
    Icon: CheckCircle2,
    icon: "text-success",
    label: "Concluído",
  },
  CRITICAL: {
    Icon: CircleAlert,
    icon: "text-destructive",
    label: "Importante",
  },
};

const COMPACT_MESSAGE_LIMIT = 132;

export default function NotificationCenter({
  isOpen,
  onClose,
  notifications = [],
  unreadCount = 0,
  readStatus = "UNREAD",
  loading = false,
  websocketConnected = false,
  dateFrom = "",
  dateTo = "",
  onReadStatusChange,
  onDateFromChange,
  onDateToChange,
  onApplyFilters,
  onClearFilters,
  onRefresh,
  onMarkRead,
  onMarkAllRead,
  onOpenNotification,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(Boolean(dateFrom || dateTo));

  const notificationGroups = useMemo(() => {
    const items = notifications.map((notification) => ({
      ...notification,
      isExpanded: expandedIds.has(notification.id),
    }));
    return groupNotifications(items);
  }, [expandedIds, notifications]);

  const hasDateFilters = Boolean(dateFrom || dateTo);

  const toggleExpanded = (notificationId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      next.has(notificationId) ? next.delete(notificationId) : next.add(notificationId);
      return next;
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="max-w-[min(100vw,620px)] overflow-hidden bg-card">
        {/* Cabeçalho em uma linha, alinhado ao X do painel (top-4, h-9). Saíram o sino grande com brilho,
            o subtítulo e a linha de conexão: o estado da conexão virou o ponto ao lado do título. */}
        <header className="border-b border-border px-5 py-4 sm:px-6">
          <div className="flex h-9 items-center gap-2 pr-11">
            <h2 className="font-display text-lg font-semibold text-foreground">Notificações</h2>
            {unreadCount > 0 ? (
              <span className="rounded-full bg-badge-danger px-2 py-0.5 text-[10px] font-bold tabular-nums text-badge-foreground">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", websocketConnected ? "bg-success" : "bg-muted-foreground/50")}
              title={websocketConnected ? "Atualizações em tempo real" : "Conexão em tempo real indisponível"}
              aria-label={websocketConnected ? "Atualizações em tempo real" : "Conexão em tempo real indisponível"}
            />
            <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading} className="ml-auto h-9 w-9 rounded-xl" title="Atualizar notificações">
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-border px-5 py-3 sm:px-6">
            <div className="flex items-center gap-2">
              <div className="grid flex-1 grid-cols-2 rounded-xl bg-muted/70 p-1">
                <TabButton active={readStatus === "UNREAD"} onClick={() => onReadStatusChange("UNREAD")}>
                  Não lidas
                  {unreadCount > 0 ? <span className="ml-1 tabular-nums opacity-65">{unreadCount}</span> : null}
                </TabButton>
                <TabButton active={readStatus === "READ"} onClick={() => onReadStatusChange("READ")}>Lidas</TabButton>
              </div>
              <Button
                variant={filtersOpen || hasDateFilters ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setFiltersOpen((current) => !current)}
                className="relative h-9 w-9 shrink-0 rounded-xl"
                aria-label="Filtrar notificações"
              >
                <Filter className="h-4 w-4" />
                {hasDateFilters ? <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" /> : null}
              </Button>
            </div>

            {filtersOpen ? (
              <div className="mt-3 rounded-2xl border border-border/80 bg-card p-3 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-foreground">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  Filtrar por período
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <DateField label="Data inicial" value={dateFrom} onChange={(event) => onDateFromChange(event.target.value)} />
                  <DateField label="Data final" value={dateTo} onChange={(event) => onDateToChange(event.target.value)} />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={onClearFilters} disabled={!hasDateFilters}>Limpar</Button>
                  <Button variant="gold" size="sm" onClick={onApplyFilters}>Aplicar período</Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            {loading ? (
              <LoadingState />
            ) : notificationGroups.length === 0 ? (
              <EmptyState readStatus={readStatus} hasDateFilters={hasDateFilters} onClearFilters={onClearFilters} />
            ) : (
              <div className="space-y-6">
                {notificationGroups.map((group) => (
                  <section key={group.label}>
                    <div className="mb-2.5 flex items-center gap-2">
                      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{group.label}</p>
                      <span className="h-px flex-1 bg-border/70" />
                    </div>
                    <div className="space-y-2.5">
                      {group.items.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onExpand={() => toggleExpanded(notification.id)}
                          onMarkRead={() => onMarkRead(notification.id)}
                          onOpen={() => onOpenNotification?.(notification)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {readStatus === "UNREAD" && unreadCount > 0 ? (
            <footer className="border-t border-border px-5 py-2.5 sm:px-6">
              <Button variant="ghost" size="sm" onClick={onMarkAllRead} disabled={loading} className="w-full">
                <CheckCheck className="h-4 w-4" />
                Marcar todas como lidas
              </Button>
            </footer>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NotificationItem({ notification, onExpand, onMarkRead, onOpen }) {
  const isRead = Boolean(notification?.read);
  const payload = notification?.payload || {};
  const severity = SEVERITY_STYLES[String(notification?.severity || "INFO").toUpperCase()] || SEVERITY_STYLES.INFO;
  const SeverityIcon = severity.Icon;
  const taskLabel = resolveTaskLabel(notification);
  const compactMessage = truncateText(notification.message, COMPACT_MESSAGE_LIMIT);
  const eventLabel = notification.eventLabel && notification.eventLabel !== notification.title ? notification.eventLabel : "";

  return (
    // Neutro como o resto do sistema: a não lida se distingue pelo fundo um pouco mais claro e pelo
    // ponto, não por borda, véu e brilho dourados. A severidade fica só na cor do ícone.
    <article className={cn("rounded-xl border border-border p-3.5 transition-colors hover:border-foreground/20", isRead ? "bg-card" : "bg-muted/40")}>
      <div className="flex items-start gap-3">
        <div className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-muted", severity.icon)} title={severity.label}>
          <SeverityIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={cn("text-sm leading-5", isRead ? "font-medium text-muted-foreground" : "font-semibold text-foreground")}>
                  {notification.title || "Notificação"}
                </h3>
                {!isRead ? <span className="h-1.5 w-1.5 rounded-full bg-badge-danger" title="Não lida" /> : null}
              </div>
              {/* Subtítulo só quando acrescenta algo: "Parceiro inativado" embaixo de "Parceiro inativado" é ruído. */}
              {eventLabel ? <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">{eventLabel}</p> : null}
            </div>
            <time className="shrink-0 text-[11px] text-muted-foreground" dateTime={notification.createdAt || undefined}>
              {formatRelativeTime(notification.createdAt)}
            </time>
          </div>

          <p className={cn("mt-1.5 text-[13px] leading-5", isRead ? "text-muted-foreground" : "text-foreground/85")}>
            {notification.isExpanded ? notification.message || "Sem detalhes adicionais." : compactMessage}
          </p>

          {notification.isExpanded ? (
            <div className="mt-3 grid gap-x-4 gap-y-2 rounded-lg bg-muted/60 p-3 text-xs sm:grid-cols-2">
              <ExpandedMeta label="Quem fez" value={payload.actorName || payload.creatorName || "Sistema"} />
              {payload.assigneeName ? <ExpandedMeta label="Responsável" value={payload.assigneeName} /> : null}
              <ExpandedMeta label="Criada em" value={formatDateTime(notification.createdAt)} />
              {payload.taskTypeName ? <ExpandedMeta label="Tipo" value={payload.taskTypeName} /> : null}
              {notification.readAt ? <ExpandedMeta label="Lida em" value={formatDateTime(notification.readAt)} /> : null}
              {payload.observacoes ? <ExpandedMeta label="Observação" value={payload.observacoes} /> : null}
            </div>
          ) : null}

          <div className="-ml-2 mt-2.5 flex flex-wrap items-center gap-1">
            {taskLabel ? (
              <button type="button" onClick={onOpen} className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-[11px] font-semibold text-foreground transition hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {taskLabel}<ExternalLink className="h-3 w-3" />
              </button>
            ) : null}
            {!isRead ? (
              <button type="button" onClick={onMarkRead} className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Check className="h-3.5 w-3.5" /> Marcar como lida
              </button>
            ) : null}
            <button type="button" onClick={onExpand} className="ml-auto inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-expanded={notification.isExpanded}>
              {notification.isExpanded ? "Menos detalhes" : "Ver detalhes"}
              {notification.isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function TabButton({ active, onClick, children }) {
  return <button type="button" onClick={onClick} className={cn("h-8 rounded-lg px-3 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring", active ? "bg-card text-foreground shadow-sm ring-1 ring-border/70" : "text-muted-foreground hover:text-foreground")}>{children}</button>;
}

function DateField({ label, value, onChange }) {
  return (
    <label className="grid gap-1.5 text-[11px] font-semibold text-muted-foreground">
      {label}
      <input type="date" value={value} onChange={onChange} className="h-9 rounded-lg border border-input bg-background px-3 text-xs font-medium text-foreground outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20" />
    </label>
  );
}

function LoadingState() {
  return <div className="grid place-items-center px-4 py-20 text-center"><div className="grid h-12 w-12 place-items-center rounded-2xl bg-muted"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div><p className="mt-3 text-sm font-medium text-foreground">Buscando atualizações</p><p className="mt-1 text-xs text-muted-foreground">Isso deve levar só alguns segundos.</p></div>;
}

function EmptyState({ readStatus, hasDateFilters, onClearFilters }) {
  return (
    <div className="grid place-items-center px-4 py-16 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl border border-border bg-muted/50 text-muted-foreground"><Inbox className="h-6 w-6" /></div>
      <h3 className="mt-4 text-sm font-semibold text-foreground">{readStatus === "UNREAD" ? "Tudo em dia por aqui" : "Nenhuma notificação lida"}</h3>
      <p className="mt-1 max-w-xs text-xs leading-5 text-muted-foreground">{hasDateFilters ? "Não encontramos resultados no período selecionado." : readStatus === "UNREAD" ? "Quando surgir algo novo, você verá primeiro nesta lista." : "As notificações marcadas como lidas aparecerão aqui."}</p>
      {hasDateFilters ? <Button variant="ghost" size="sm" onClick={onClearFilters} className="mt-3">Limpar período</Button> : null}
    </div>
  );
}

function ExpandedMeta({ label, value }) {
  return <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p><p className="mt-0.5 break-words text-foreground/85">{value}</p></div>;
}

function groupNotifications(notifications) {
  const groups = new Map();
  notifications.forEach((notification) => {
    const label = getDateGroup(notification?.createdAt);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(notification);
  });
  return Array.from(groups, ([label, items]) => ({ label, items }));
}

function getDateGroup(value) {
  if (!value) return "Anteriores";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Anteriores";
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startToday - startDate) / 86400000);
  if (diffDays === 0) return "Hoje";
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7 && diffDays > 1) return "Esta semana";
  return new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(date);
}

function resolveTaskLabel(notification) {
  const payload = notification?.payload || {};
  if (payload?.taskLabel) return String(payload.taskLabel);
  const rawTaskId = payload?.taskId || notification?.entityId;
  if (rawTaskId && (notification?.entityType === "TAREFA" || payload?.taskId || notification?.route === "/tarefas")) return `Tarefa #${rawTaskId}`;
  return null;
}

function truncateText(value, maxLength) {
  const text = String(value || "");
  if (!text || text.length <= maxLength) return text || "Sem detalhes adicionais.";
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (diffMinutes < 1) return "agora";
  if (diffMinutes < 60) return `há ${diffMinutes} min`;
  if (diffMinutes < 1440) return `há ${Math.floor(diffMinutes / 60)} h`;
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" }).format(date).replace(".", "");
}

function formatDateTime(value) {
  if (!value) return "Não registrado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
}
